import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react'
import { useUsers } from './user-context'

const WorkflowContext = createContext(undefined)

export function WorkflowProvider({ children }) {
  const [workflows, setWorkflows] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedWorkflows = localStorage.getItem('workflows')
      return savedWorkflows ? JSON.parse(savedWorkflows) : []
    }
    return []
  })
  const [currentWorkflow, setCurrentWorkflow] = useState(null)
  const { currentUser } = useUsers()

  // Sync workflows from backend when user or permissions change
  useEffect(() => {
    const syncWorkflowsFromBackend = async () => {
      // Only sync if user is authenticated and has roles
      if (!currentUser || !currentUser.roles || currentUser.roles.length === 0) {
        // Clear workflows if no user or no roles
        setWorkflows([])
        return
      }

      // Check if user has admin role or any workflow view permission
      const hasWorkflowPermission = currentUser.roles.includes('admin') || 
                                   currentUser.permissions?.includes('view_workflows') ||
                                   currentUser.permissions?.includes('view_all_workflows') ||
                                   currentUser.permissions?.includes('view_own_workflows') ||
                                   currentUser.permissions?.includes('view_department_workflows')
      
      if (!hasWorkflowPermission) {
        // Clear workflows if user has no workflow permissions
        setWorkflows([])
        return
      }

      try {
        const response = await fetch('/workflows', {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        })
        
        if (response.ok) {
          const contentType = response.headers.get('content-type')
          if (contentType && contentType.includes('application/json')) {
            const data = await response.json();
            
            // Transform backend workflows to match frontend format
            const backendWorkflows = data.workflows.map(workflow => ({
              id: workflow.id.toString(),
              name: workflow.name,
              description: workflow.description,
              status: workflow.status || 'draft',
              nodes: [], // Will be loaded via loadWorkflowData when needed
              edges: [], // Will be loaded via loadWorkflowData when needed
              createdAt: workflow.created_at,
              lastRun: workflow.last_run || null,
              triggerType: workflow.trigger_type || "manual",
              updatedAt: workflow.updated_at,
              creator: workflow.owner?.name || 'Unknown User',
              viewMode: 'graph',
              // Assignment fields
              assignment_type: workflow.assignment_type || null,
              assigned_user_ids: workflow.assigned_user_ids || null,
              assigned_department_ids: workflow.assigned_department_ids || null
            }))
                        
            // REPLACE workflows state with backend workflows
            setWorkflows(backendWorkflows)
            
            // If we have a current workflow, make sure its data is loaded
            if (currentWorkflow) {
              const currentWorkflowInBackend = backendWorkflows.find(w => w.id === currentWorkflow.id)
              if (currentWorkflowInBackend && (!currentWorkflowInBackend.nodes || currentWorkflowInBackend.nodes.length === 0)) {
                // Load the data for the current workflow
                loadWorkflowData(currentWorkflow.id)
              }
            }
          }
        }
      } catch (error) {
        console.error('Failed to sync workflows from backend:', error)
        // Clear workflows on error
        setWorkflows([])
      }
    }
    
    // Always sync when currentUser changes (user login/logout/switch)
    if (currentUser) {
      syncWorkflowsFromBackend()
    } else {
      // Clear workflows when no user
      setWorkflows([])
    }
  }, [currentUser]) // Only depend on currentUser, not workflows.length

  // Only save to localStorage when workflows are not empty (user has permissions)
  useEffect(() => {
    if (typeof window !== 'undefined' && workflows.length > 0) {
      localStorage.setItem('workflows', JSON.stringify(workflows))
    }
  }, [workflows])

  // Add a module-level lock to prevent concurrent workflow creation
  let createWorkflowLock = false;

  // Load workflow nodes and edges from database
  const loadWorkflowData = async (workflowId) => {
    try {
      const response = await fetch(`/workflows/${workflowId}/nodes-and-edges`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
      
      if (response.ok) {
        const { nodes, edges } = await response.json()
        
        // Clean up any temporary edges that might conflict with database edges
        const cleanedEdges = edges.map(edge => {
          // Ensure edge IDs are properly formatted
          if (edge.id && !edge.id.startsWith('edge-')) {
            return { ...edge, id: `edge-${edge.id}` }
          }
          return edge
        })
        
        // Update the workflow in state with loaded nodes and edges
        setWorkflows(prev => {
          const updated = prev.map(w => 
            w.id === workflowId.toString() 
              ? { ...w, nodes, edges: cleanedEdges, updatedAt: new Date().toISOString() }
              : w
          );
          return updated;
        })
        
        // Update current workflow if it's the active one
        if (currentWorkflow?.id === workflowId.toString()) {
          setCurrentWorkflow(prev => 
            prev ? { ...prev, nodes, edges: cleanedEdges, updatedAt: new Date().toISOString() } : null
          )
        }
        
        return { nodes, edges: cleanedEdges }
      } else {
        console.error(`Failed to load workflow data: ${response.status}`)
      }
    } catch (error) {
      console.error('Error in loadWorkflowData:', error)
    }
    return { nodes: [], edges: [] }
  }

  // Debounced save function for auto-saving visual changes
  const debouncedSave = React.useCallback(
    debounce(async (workflowId, nodes, edges, csrf_token) => {
      try {
        const csrfToken = csrf_token;

        await fetch(`/workflows/${workflowId}/nodes-and-edges`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'Accept': 'application/json',
          },
          body: JSON.stringify({ nodes, edges }),
        })
      } catch (error) {
        console.error('Failed to auto-save workflow data:', error)
      }
    }, 1000),
    []
  )

  // Debounce utility function
  function debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  const createWorkflow = async (name, csrf_token, assignmentType = 'individual', assignedUserIds = null) => {
    if (createWorkflowLock) {
      // Prevent concurrent calls
      return null;
    }
    createWorkflowLock = true;
    
    try {
      // Get CSRF token from meta tag or use a fallback
      const csrfToken = csrf_token;
      
      // Default to current user if no assigned user IDs provided and assignment type is individual
      const userIds = assignedUserIds || (assignmentType === 'individual' && currentUser?.id ? [currentUser.id] : []);
      
      const requestPayload = {
        name: name,
        description: 'A new workflow',
        owner_id: currentUser?.id || 1, // Default to user ID 1 if not available
        assignment_type: assignmentType,
        assigned_user_ids: userIds,
      };
      
      const response = await fetch('/workflows', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestPayload),
      })
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to create workflow in backend: ${response.status}`)
      }
      
      const backendWorkflow = await response.json();
      
      // Create the workflow object with the backend ID
      // Note: Nodes will be loaded by Builder from the default trigger created by backend
      const newWorkflow = {
        id: backendWorkflow.id.toString(), // Convert to string for consistency
        name: backendWorkflow.name,
        description: backendWorkflow.description,
        status: backendWorkflow.status || 'draft',
        nodes: [], // Will be populated by Builder from database triggers/steps
        edges: [], // Will be populated by Builder from database edges
        createdAt: backendWorkflow.created_at || new Date().toISOString(),
        updatedAt: backendWorkflow.updated_at || new Date().toISOString(),
        creator: currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Unknown User',
        viewMode: 'graph'
      }

      // Prevent duplicate workflows by ID
      setWorkflows((prev) => {
        if (prev.some(w => w.id === newWorkflow.id)) {
          return prev;
        }
        return [...prev, newWorkflow];
      })
      setCurrentWorkflow(newWorkflow)
      
      return newWorkflow
    } catch (error) {
      console.error('Failed to create workflow:', error)
      // Only add fallback workflow if backend call fails
      // Note: This fallback won't have a default trigger since backend call failed
      const fallbackWorkflow = {
        id: `wf-${Date.now()}`,
        name,
        description: 'A new workflow',
        status: 'draft',
        nodes: [], // No default nodes for fallback workflow
        edges: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        creator: currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Unknown User',
        viewMode: 'graph'
      }
      
      setWorkflows((prev) => {
        if (prev.some(w => w.id === fallbackWorkflow.id)) {
          return prev;
        }
        return [...prev, fallbackWorkflow];
      })
      setCurrentWorkflow(fallbackWorkflow)
      
      return fallbackWorkflow
    } finally {
      createWorkflowLock = false;
    }
  }

  const updateWorkflow = async (workflowId, updates, skipApiCall = false, csrf_token) => {
    // Immediately update the local state for instant UI feedback
    setWorkflows((prev) =>
      prev.map((workflow) =>
        workflow.id === workflowId
          ? { ...workflow, ...updates, updatedAt: new Date().toISOString() }
          : workflow
      )
    )

    if (currentWorkflow?.id === workflowId) {
      setCurrentWorkflow((prev) => (prev ? { ...prev, ...updates, updatedAt: new Date().toISOString() } : null))
    }

    // Skip API call if requested (when API call is handled elsewhere)
    if (skipApiCall) {
      return
    }

    // Call backend API to persist changes
    try {
      // Get CSRF token
      const csrfToken = csrf_token;

      // Prepare the request payload - only include fields that are actually being updated
      const payload = {};
      if (updates.name !== undefined) payload.name = updates.name;
      if (updates.description !== undefined) payload.description = updates.description;
      if (updates.status !== undefined) payload.status = updates.status;

      const response = await fetch(`/workflows/${workflowId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Revert local changes if backend update failed
        setWorkflows((prev) =>
          prev.map((workflow) =>
            workflow.id === workflowId
              ? { ...workflow, updatedAt: new Date().toISOString() }
              : workflow
          )
        )

        if (currentWorkflow?.id === workflowId) {
          setCurrentWorkflow((prev) => (prev ? { ...prev, updatedAt: new Date().toISOString() } : null))
        }

        throw new Error(`Failed to update workflow in backend: ${response.status} ${response.statusText}`);
      }

      const updatedWorkflow = await response.json();

      // Update local state with the response from backend to ensure consistency
      setWorkflows((prev) =>
        prev.map((workflow) =>
          workflow.id === workflowId
            ? {
                ...workflow,
                name: updatedWorkflow.name,
                description: updatedWorkflow.description,
                status: updatedWorkflow.status,
                updatedAt: updatedWorkflow.updated_at || new Date().toISOString(),
              }
            : workflow
        )
      )

      if (currentWorkflow?.id === workflowId) {
        setCurrentWorkflow((prev) =>
          prev ? {
            ...prev,
            name: updatedWorkflow.name,
            description: updatedWorkflow.description,
            status: updatedWorkflow.status,
            updatedAt: updatedWorkflow.updated_at || new Date().toISOString(),
          } : null
        )
      }

    } catch (error) {
      console.error('Error updating workflow:', error);
      
      // Optionally show user feedback about the error
      // You could integrate with a toast notification system here
      console.warn('Workflow update failed - local changes may not be persisted');
    }
  }

  const workflowIdsMatch = (leftId, rightId) =>
    leftId != null && rightId != null && leftId.toString() === rightId.toString()

  const resolveWorkflowForCanvas = (workflowId) => {
    if (workflowId == null) return null

    const fromList = workflows.find((w) => workflowIdsMatch(w.id, workflowId))
    const fromCurrent =
      currentWorkflow && workflowIdsMatch(currentWorkflow.id, workflowId)
        ? currentWorkflow
        : null

    if (
      fromCurrent &&
      ((fromCurrent.nodes?.length ?? 0) > 0 || (fromCurrent.edges?.length ?? 0) > 0)
    ) {
      return fromCurrent
    }

    return fromList || fromCurrent
  }

  const addNode = async (workflowId, node, csrf_token, options = {}) => {
    const skipLocalState = Boolean(options.skipLocalState)
    const requirePersistence = Boolean(options.requirePersistence)
    let persistedToDatabase = false
    let persistenceError = null

    // Save to backend database if it's a task node
    if (node.type === 'taskNode') {
      try {
        const csrfToken = csrf_token;
        
        const stepData = {
          name: node.data.name || 'New Task',
          description: node.data.description || '',
          step_type: 'task',
          link: node.data.link || '',
          category: node.data.category || 'General',
          priority: parseInt(node.data.priority) || 5,
          due: node.data.due || null,
          status: node.data.status || 'To Do',
          deferrable: Boolean(node.data.deferrable),
          missable: Boolean(node.data.missable),
          task_type: node.data.taskTypeCategories || [],
          position: node.position,
          node_id: node.id,
          workflow_id: workflowId,
          assignee_id: node.data.assignee?.id || currentUser?.id || null,
          assignment: node.data.assignment || null,
          options: {
            expanded: true,
            autoLayoutMode: false,
            handlePosition: 'top-bottom',
            dynamicDateFormat: 'iso',
          }
        }

        const response = await fetch(`/workflows/${workflowId}/steps`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
          },
          body: JSON.stringify(stepData),
        })

        if (!response.ok) {
          throw new Error(`Failed to save step: ${response.status}`)
        }

        const savedStep = await response.json()
        persistedToDatabase = true
        
        // Update the node with the saved data
        node = {
          ...node,
          id: savedStep.node_id || `step-${savedStep.id}`,
          databaseId: savedStep.id,
          data: {
            ...node.data,
            name: savedStep.name,
            description: savedStep.description,
            category: savedStep.category,
            priority: savedStep.priority,
            due: savedStep.due,
            status: savedStep.status,
            deferrable: savedStep.deferrable,
            missable: savedStep.missable,
            taskTypeCategories: savedStep.task_type,
            assignee: savedStep.assignee
          }
        }
        
      } catch (error) {
        persistenceError = error?.message || 'taskNode save failed'
        console.error('Failed to save step to database:', error)
        // Continue with local storage even if database save fails
      }
    }

    if (node.type === 'requestNode') {
      try {
        const csrfToken = csrf_token;
        
        const stepData = {
          name: node.data.name || 'HTTP Request',
          description: node.data.description || '',
          step_type: 'request',
          position: node.position,
          node_id: node.id,
          workflow_id: workflowId,
          url: node.data.url || '',
          method: node.data.method || 'GET',
          headers: node.data.headers || [],
          body: node.data.body || '',
          response: node.data.response || null,
          extracted_variables: node.data.extractedVariables || [],
          options: {
            expanded: true,
            autoLayoutMode: false,
            handlePosition: 'top-bottom',
            dynamicDateFormat: 'iso',
          }
        }

        const response = await fetch(`/workflows/${workflowId}/steps`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
          },
          body: JSON.stringify(stepData),
        })

        if (!response.ok) {
          throw new Error(`Failed to save step: ${response.status}`)
        }

        const savedStep = await response.json()
        persistedToDatabase = true
        
        // Update the node with the saved data
        node = {
          ...node,
          id: savedStep.node_id || `step-${savedStep.id}`,
          databaseId: savedStep.id,
          data: {
            ...node.data,
            name: savedStep.name,
            description: savedStep.description,
            step_type: savedStep.step_type,
            url: savedStep.url,
            method: savedStep.method,
            headers: savedStep.headers,
            body: savedStep.body,
            response: savedStep.response,
            extractedVariables: savedStep.extracted_variables
          }
        }
        
      } catch (error) {
        persistenceError = error?.message || 'requestNode save failed'
        console.error('Failed to save step to database:', error)
        // Continue with local storage even if database save fails
      }
    }

    if (node.type === 'formNode') {
      try {
        const csrfToken = csrf_token;
        
        const stepData = {
          name: node.data.name || 'Dynamic Form',
          description: node.data.description || '',
          step_type: 'form',
          position: JSON.stringify(node.position),
          node_id: node.id,
          workflow_id: workflowId,
          fields: node.data.fields || [],
          settings: node.data.settings || {},
          options: {
            expanded: true,
            autoLayoutMode: false,
            handlePosition: 'top-bottom',
            dynamicDateFormat: 'iso',
          }
        }

        const response = await fetch(`/workflows/${workflowId}/steps`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
          },
          body: JSON.stringify(stepData),
        })

        if (!response.ok) {
          throw new Error(`Failed to save step: ${response.status}`)
        }

        const savedStep = await response.json()
        persistedToDatabase = true
        
        // Update the node with the saved data
        node = {
          ...node,
          id: savedStep.node_id || `step-${savedStep.id}`,
          databaseId: savedStep.id,
          data: {
            ...node.data,
            name: savedStep.name,
            description: savedStep.description,
            step_type: savedStep.step_type,
            fields: savedStep.fields,
            settings: savedStep.settings,
            form_id: savedStep.form_id // Include the form_id from the backend response
          }
        }
        
      } catch (error) {
        persistenceError = error?.message || 'formNode save failed'
        console.error('Failed to save step to database:', error)
        // Continue with local storage even if database save fails
      }
    }

    if (node.type === 'ifElseNode') {
      try {
        const csrfToken = csrf_token;
        
        const stepData = {
          name: node.data.name || 'If/Else',
          description: node.data.description || '',
          step_type: 'if_else',
          position: JSON.stringify(node.position),
          node_id: node.id,
          workflow_id: workflowId,
          conditions: node.data.conditions || [],
          options: {
            expanded: true,
            autoLayoutMode: false,
            handlePosition: 'top-bottom',
            dynamicDateFormat: 'iso',
          }
        }

        const response = await fetch(`/workflows/${workflowId}/steps`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
          },
          body: JSON.stringify(stepData),
        })

        if (!response.ok) {
          throw new Error(`Failed to save step: ${response.status}`)
        }

        const savedStep = await response.json()
        persistedToDatabase = true
        
        // Update the node with the saved data
        node = {
          ...node,
          id: savedStep.node_id || `step-${savedStep.id}`,
          databaseId: savedStep.id,
          data: {
            ...node.data,
            name: savedStep.name,
            description: savedStep.description,
            step_type: savedStep.step_type,
            conditions: savedStep.conditions
          }
        }
        
      } catch (error) {
        persistenceError = error?.message || 'ifElseNode save failed'
        console.error('Failed to save step to database:', error)
        // Continue with local storage even if database save fails
      }
    }

    if (node.type === 'waitNode') {
      try {
        const csrfToken = csrf_token;

        const stepData = {
          name: node.data.name || 'Wait',
          description: node.data.description || '',
          step_type: 'wait',
          position: JSON.stringify(node.position),
          node_id: node.id,
          workflow_id: workflowId,
          wait_duration_unit: node.data.durationUnit || node.data.wait_duration_unit || 'days',
          wait_duration_expression: node.data.durationExpression || node.data.wait_duration_expression || '1',
          wait_target_time: node.data.targetTime || node.data.wait_target_time || '08:00',
          options: {
            expanded: true,
            autoLayoutMode: false,
            handlePosition: 'top-bottom',
            dynamicDateFormat: 'iso',
          }
        }

        const response = await fetch(`/workflows/${workflowId}/steps`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
          },
          body: JSON.stringify(stepData),
        })

        if (!response.ok) {
          throw new Error(`Failed to save step: ${response.status}`)
        }

        const savedStep = await response.json()
        persistedToDatabase = true
        
        node = {
          ...node,
          id: savedStep.node_id || `step-${savedStep.id}`,
          databaseId: savedStep.id,
          data: {
            ...node.data,
            name: savedStep.name,
            description: savedStep.description,
            step_type: savedStep.step_type,
            durationUnit: savedStep.wait_duration_unit || node.data.durationUnit || 'days',
            durationExpression: savedStep.wait_duration_expression || node.data.durationExpression || '1',
            targetTime: savedStep.wait_target_time || node.data.targetTime || '08:00',
            wait_duration_unit: savedStep.wait_duration_unit || node.data.wait_duration_unit || 'days',
            wait_duration_expression: savedStep.wait_duration_expression || node.data.wait_duration_expression || '1',
            wait_target_time: savedStep.wait_target_time || node.data.wait_target_time || '08:00',
          }
        }
      } catch (error) {
        persistenceError = error?.message || 'waitNode save failed'
        console.error('Failed to save wait step to database:', error)
      }
    }

    if (node.type === 'actionNode') {
      try {
        const csrfToken = csrf_token;

        const stepSettings = {
          operation: node.data.operation || 'set_suppression',
          entity_type: node.data.entityType || 'contract',
          entity_key_source: node.data.entityKeySource || 'variable',
          entity_key_variable: node.data.entityKeyVariable || 'contractid',
          suppression_type: node.data.suppressionType || 'workflow_task_creation',
          reason_code: node.data.reasonCode || 'wait_full_project_completion',
          reason_text: node.data.reasonText || '',
          scope_type: node.data.scopeType || 'workflow',
        }

        const stepData = {
          name: node.data.name || 'Set Suppression',
          description: node.data.description || '',
          step_type: 'action',
          position: JSON.stringify(node.position),
          node_id: node.id,
          workflow_id: workflowId,
          settings: stepSettings,
          options: {
            expanded: true,
            autoLayoutMode: false,
            handlePosition: 'top-bottom',
            dynamicDateFormat: 'iso',
          }
        }

        const response = await fetch(`/workflows/${workflowId}/steps`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
          },
          body: JSON.stringify(stepData),
        })

        if (!response.ok) {
          throw new Error(`Failed to save step: ${response.status}`)
        }

        const savedStep = await response.json()
        persistedToDatabase = true

        node = {
          ...node,
          id: savedStep.node_id || `step-${savedStep.id}`,
          databaseId: savedStep.id,
          data: {
            ...node.data,
            name: savedStep.name,
            description: savedStep.description,
            step_type: savedStep.step_type,
            settings: savedStep.settings || stepSettings,
            operation: savedStep.settings?.operation || stepSettings.operation,
            entityType: savedStep.settings?.entity_type || stepSettings.entity_type,
            entityKeySource: savedStep.settings?.entity_key_source || stepSettings.entity_key_source,
            entityKeyVariable: savedStep.settings?.entity_key_variable || stepSettings.entity_key_variable,
            suppressionType: savedStep.settings?.suppression_type || stepSettings.suppression_type,
            reasonCode: savedStep.settings?.reason_code || stepSettings.reason_code,
            reasonText: savedStep.settings?.reason_text || stepSettings.reason_text,
            scopeType: savedStep.settings?.scope_type || stepSettings.scope_type,
          }
        }
      } catch (error) {
        persistenceError = error?.message || 'actionNode save failed'
        console.error('Failed to save action step to database:', error)
      }
    }

    if (node.type === 'sendTextNode') {
      try {
        const csrfToken = csrf_token;

        const stepSettings = {
          template_id: node.data.templateId ?? node.data.settings?.template_id ?? '',
          recipient_source: node.data.recipientSource ?? node.data.settings?.recipient_source ?? 'variable',
          recipient_phone: node.data.recipientPhone ?? node.data.settings?.recipient_phone ?? '',
          variable_mappings: node.data.variableMappings ?? node.data.settings?.variable_mappings ?? {},
          message_override: node.data.messageOverride ?? node.data.settings?.message_override ?? '',
          use_template_message: typeof node.data.useTemplateMessage === 'boolean'
            ? node.data.useTemplateMessage
            : Boolean(node.data.settings?.use_template_message ?? true),
          media_urls: node.data.mediaUrls ?? node.data.settings?.media_urls ?? [],
          media_source: node.data.mediaSource ?? node.data.settings?.media_source ?? 'both',
        }

        const stepData = {
          name: node.data.name || 'Send SMS',
          description: node.data.description || '',
          step_type: 'send_text',
          position: JSON.stringify(node.position),
          node_id: node.id,
          workflow_id: workflowId,
          settings: stepSettings,
          options: {
            expanded: true,
            autoLayoutMode: false,
            handlePosition: 'top-bottom',
            dynamicDateFormat: 'iso',
          }
        }

        const response = await fetch(`/workflows/${workflowId}/steps`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
          },
          body: JSON.stringify(stepData),
        })

        if (!response.ok) {
          throw new Error(`Failed to save step: ${response.status}`)
        }

        const savedStep = await response.json()
        persistedToDatabase = true

        const savedSettings = savedStep.settings || stepSettings

        node = {
          ...node,
          id: savedStep.node_id || `step-${savedStep.id}`,
          databaseId: savedStep.id,
          data: {
            ...node.data,
            name: savedStep.name,
            description: savedStep.description,
            step_type: savedStep.step_type,
            settings: savedSettings,
            templateId: savedSettings.template_id ?? '',
            recipientSource: savedSettings.recipient_source ?? 'variable',
            recipientPhone: savedSettings.recipient_phone ?? '',
            variableMappings: savedSettings.variable_mappings ?? {},
            messageOverride: savedSettings.message_override ?? '',
            useTemplateMessage: savedSettings.use_template_message ?? true,
            mediaUrls: savedSettings.media_urls ?? [],
            mediaSource: savedSettings.media_source ?? 'both',
          }
        }
      } catch (error) {
        persistenceError = error?.message || 'sendTextNode save failed'
        console.error('Failed to save send-text step to database:', error)
      }
    }

    if (node.type === 'sendEmailNode') {
      try {
        const csrfToken = csrf_token;

        const stepSettings = {
          template_id: node.data.templateId ?? node.data.settings?.template_id ?? '',
          email_account_id: node.data.emailAccountId ?? node.data.settings?.email_account_id ?? '',
          recipient_source: node.data.recipientSource ?? node.data.settings?.recipient_source ?? 'variable',
          recipient_email: node.data.recipientEmail ?? node.data.settings?.recipient_email ?? '',
          variable_mappings: node.data.variableMappings ?? node.data.settings?.variable_mappings ?? {},
          message_override: node.data.messageOverride ?? node.data.settings?.message_override ?? '',
          subject_override: node.data.subjectOverride ?? node.data.settings?.subject_override ?? '',
          use_template_message: typeof node.data.useTemplateMessage === 'boolean'
            ? node.data.useTemplateMessage
            : Boolean(node.data.settings?.use_template_message ?? true),
          use_template_subject: typeof node.data.useTemplateSubject === 'boolean'
            ? node.data.useTemplateSubject
            : Boolean(node.data.settings?.use_template_subject ?? true),
        }

        const stepData = {
          name: node.data.name || 'Send Email',
          description: node.data.description || '',
          step_type: 'send_email',
          position: JSON.stringify(node.position),
          node_id: node.id,
          workflow_id: workflowId,
          settings: stepSettings,
          options: {
            expanded: true,
            autoLayoutMode: false,
            handlePosition: 'top-bottom',
            dynamicDateFormat: 'iso',
          }
        }

        const response = await fetch(`/workflows/${workflowId}/steps`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json',
          },
          body: JSON.stringify(stepData),
        })

        if (!response.ok) {
          throw new Error(`Failed to save step: ${response.status}`)
        }

        const savedStep = await response.json()
        persistedToDatabase = true

        const savedSettings = savedStep.settings || stepSettings

        node = {
          ...node,
          id: savedStep.node_id || `step-${savedStep.id}`,
          databaseId: savedStep.id,
          data: {
            ...node.data,
            name: savedStep.name,
            description: savedStep.description,
            step_type: savedStep.step_type,
            settings: savedSettings,
            templateId: savedSettings.template_id ?? '',
            emailAccountId: savedSettings.email_account_id ?? '',
            recipientSource: savedSettings.recipient_source ?? 'variable',
            recipientEmail: savedSettings.recipient_email ?? '',
            variableMappings: savedSettings.variable_mappings ?? {},
            messageOverride: savedSettings.message_override ?? '',
            subjectOverride: savedSettings.subject_override ?? '',
            useTemplateMessage: savedSettings.use_template_message ?? true,
            useTemplateSubject: savedSettings.use_template_subject ?? true,
          }
        }
      } catch (error) {
        persistenceError = error?.message || 'sendEmailNode save failed'
        console.error('Failed to save send-email step to database:', error)
      }
    }

    if (requirePersistence && !persistedToDatabase) {
      return null
    }

    if (!skipLocalState) {
      setWorkflows((prev) =>
        prev.map((workflow) =>
          workflowIdsMatch(workflow.id, workflowId)
            ? {
                ...workflow,
                nodes: [...(workflow.nodes ?? []), node],
                updatedAt: new Date().toISOString(),
              }
            : workflow
        )
      )

      if (workflowIdsMatch(currentWorkflow?.id, workflowId)) {
        setCurrentWorkflow((prev) =>
          prev
            ? {
                ...prev,
                nodes: [...(prev.nodes ?? []), node],
                updatedAt: new Date().toISOString(),
              }
            : null
        )
      }
    }

    // Return the updated node with database ID
    return node
  }

  const updateNode = (workflowId, nodeId, updates) => {
    setWorkflows((prev) =>
      prev.map((workflow) =>
        workflowIdsMatch(workflow.id, workflowId)
          ? {
              ...workflow,
              nodes: workflow.nodes.map((node) =>
                node.id === nodeId ? { ...node, ...updates } : node
              ),
              updatedAt: new Date().toISOString(),
            }
          : workflow
      )
    )

    if (workflowId != null && workflowIdsMatch(currentWorkflow?.id, workflowId)) {
      setCurrentWorkflow((prev) =>
        prev && typeof prev === 'object' && Array.isArray(prev.nodes)
          ? {
              ...prev,
              nodes: prev.nodes.map((node) =>
                node.id === nodeId ? { ...node, ...updates } : node
              ),
              updatedAt: new Date().toISOString(),
            }
          : prev
      )
    }
  }

  const addEdge = async (workflowId, edge, csrf_token) => {
    // Check if this edge already exists to prevent duplicates
    const workflow = resolveWorkflowForCanvas(workflowId)
    if (workflow) {
      const workflowEdges = workflow.edges ?? []
      const workflowNodes = workflow.nodes ?? []

      const existingEdge = workflowEdges.find(e => 
        e.source === edge.source && e.target === edge.target
      )
      
      if (existingEdge) {
        console.log('Edge already exists in workflow context, skipping:', edge)
        return
      }

      const sourceNode = workflowNodes.find((n) => n.id === edge.source)
      const targetNode = workflowNodes.find((n) => n.id === edge.target)
      
      // If source is a task node and target is a form node, update the task node's post_completion_form_id
      if (sourceNode?.type === 'taskNode' && targetNode?.type === 'formNode') {
        
        // Get the form ID from the form node's data (this is the actual form ID from the forms table)
        // The form_id should be stored in the form node's data after it's saved to the database
        const formId = targetNode.data?.form_id
        
        // Only update if we have a valid form ID
        if (formId) {
          console.log('Connecting task to form with ID:', formId)
          
          // Update the task node's settings to include the form ID
          const updatedTaskNode = {
            ...sourceNode,
            data: {
              ...sourceNode.data,
              settings: {
                ...sourceNode.data.settings,
                post_completion_form_id: formId.toString()
              }
            }
          }
        
        // Update the node in the workflow context
        updateNode(workflowId, sourceNode.id, { data: updatedTaskNode.data })
        
        // Also update the local state immediately
        setWorkflows((prev) =>
          prev.map((w) =>
            workflowIdsMatch(w.id, workflowId)
              ? {
                  ...w,
                  nodes: (w.nodes ?? []).map((n) => n.id === sourceNode.id ? updatedTaskNode : n),
                  updatedAt: new Date().toISOString(),
                }
              : w
          )
        )

        if (workflowIdsMatch(currentWorkflow?.id, workflowId)) {
          setCurrentWorkflow((prev) =>
            prev
              ? {
                  ...prev,
                  nodes: prev.nodes.map((n) => n.id === sourceNode.id ? updatedTaskNode : n),
                  updatedAt: new Date().toISOString(),
                }
              : null
          )
        }

        // Save the updated task node to the database
        try {
          const csrfToken = csrf_token;

          // Get the actual database ID for the task node
          let actualNodeId = sourceNode.id;
          if (sourceNode.id.startsWith('step-')) {
            actualNodeId = sourceNode.id.replace('step-', '');
          } else if (sourceNode.databaseId) {
            actualNodeId = sourceNode.databaseId;
          }

                  const stepData = {
          step_type: 'task',
          workflow_id: workflowId,
          name: updatedTaskNode.data.name,
          description: updatedTaskNode.data.description,
          category: updatedTaskNode.data.category,
          priority: updatedTaskNode.data.priority,
          due: updatedTaskNode.data.due,
          status: updatedTaskNode.data.status,
          deferrable: updatedTaskNode.data.deferrable,
          missable: updatedTaskNode.data.missable,
          task_type: updatedTaskNode.data.taskTypeCategories || [],
          assignee_id: updatedTaskNode.data.assignee_id,
          link: updatedTaskNode.data.link || '',
          link_title: updatedTaskNode.data.link_title || '',
          settings: updatedTaskNode.data.settings,
          checklist: updatedTaskNode.data.checklist || []
        }

          const response = await fetch(`/workflows/${workflowId}/steps/${actualNodeId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-TOKEN': csrfToken,
              'Accept': 'application/json',
            },
            body: JSON.stringify(stepData),
          })

          if (!response.ok) {
            throw new Error(`Failed to update task node: ${response.status}`)
          }

        } catch (error) {
          console.error('Failed to save task node update to database:', error)
        }
        } else {
          console.warn('Cannot connect task to form: No valid form ID found in form node data')
        }
      }
    }

    setWorkflows((prev) =>
      prev.map((workflow) =>
        workflowIdsMatch(workflow.id, workflowId)
          ? {
              ...workflow,
              edges: [...(workflow.edges ?? []), edge],
              updatedAt: new Date().toISOString(),
            }
          : workflow
      )
    )

    if (workflowIdsMatch(currentWorkflow?.id, workflowId)) {
      setCurrentWorkflow((prev) =>
        prev
          ? {
              ...prev,
              edges: [...(prev.edges ?? []), edge],
              updatedAt: new Date().toISOString(),
            }
          : null
      )
    }
  }

  const updateEdge = (workflowId, edgeId, updates) => {
    setWorkflows((prev) =>
      prev.map((workflow) =>
        workflowIdsMatch(workflow.id, workflowId)
          ? {
              ...workflow,
              edges: (workflow.edges ?? []).map((edge) =>
                edge.id === edgeId ? { ...edge, ...updates } : edge
              ),
              updatedAt: new Date().toISOString(),
            }
          : workflow
      )
    )

    if (workflowIdsMatch(currentWorkflow?.id, workflowId)) {
      setCurrentWorkflow((prev) =>
        prev
          ? {
              ...prev,
              edges: (prev.edges ?? []).map((edge) =>
                edge.id === edgeId ? { ...edge, ...updates } : edge
              ),
              updatedAt: new Date().toISOString(),
            }
          : null
      )
    }
  }

  const addNodes = (workflowId, nodes) => {
    setWorkflows((prev) =>
      prev.map((workflow) =>
        workflowIdsMatch(workflow.id, workflowId)
          ? {
              ...workflow,
              nodes: [...(workflow.nodes ?? []), ...nodes],
              updatedAt: new Date().toISOString(),
            }
          : workflow
      )
    )

    if (workflowIdsMatch(currentWorkflow?.id, workflowId)) {
      setCurrentWorkflow((prev) =>
        prev
          ? {
              ...prev,
              nodes: [...(prev.nodes ?? []), ...nodes],
              updatedAt: new Date().toISOString(),
            }
          : null
      )
    }
  }

  const addEdges = (workflowId, edges) => {
    setWorkflows((prev) =>
      prev.map((workflow) =>
        workflow.id === workflowId
          ? {
              ...workflow,
              edges: [...workflow.edges, ...edges],
              updatedAt: new Date().toISOString(),
            }
          : workflow
      )
    )

    if (currentWorkflow?.id === workflowId) {
      setCurrentWorkflow((prev) =>
        prev
          ? {
              ...prev,
              edges: [...prev.edges, ...edges],
              updatedAt: new Date().toISOString(),
            }
          : null
      )
    }
  }

  const validateConnection = (workflowId, sourceId, targetId) => {
    const workflow = workflows.find((w) => w.id === workflowId)
    if (!workflow) return false

    // Check if both nodes exist
    const sourceNode = workflow.nodes.find((n) => n.id === sourceId)
    const targetNode = workflow.nodes.find((n) => n.id === targetId)
    if (!sourceNode || !targetNode) return false

    // Check if connection already exists
    const existingConnection = workflow.edges.find(
      (e) => e.source === sourceId && e.target === targetId
    )
    if (existingConnection) return false

    // Check if target node is not a trigger node (trigger nodes can only be sources)
    if (targetNode.type === 'triggerNode') return false

    return true
  }

  const setCurrentWorkflowById = (workflowIdOrSelection) => {
    if (typeof workflowIdOrSelection === 'function') {
      setCurrentWorkflow(workflowIdOrSelection)
      return
    }

    let workflowId = workflowIdOrSelection
    let overrides = null

    if (typeof workflowIdOrSelection === 'object' && workflowIdOrSelection !== null) {
      workflowId = workflowIdOrSelection.id?.toString()
      overrides = workflowIdOrSelection
    } else if (workflowIdOrSelection != null) {
      workflowId = workflowIdOrSelection.toString()
    }

    if (!workflowId) {
      setCurrentWorkflow(null)
      return
    }

    const workflow = workflows.find((w) => w.id === workflowId)
    const baseWorkflow = workflow || overrides

    if (!baseWorkflow) {
      setCurrentWorkflow(null)
      return
    }

    const mergedWorkflow = {
      ...(workflow || {}),
      ...(overrides || {}),
      id: workflowId,
      nodes: overrides?.nodes ?? workflow?.nodes ?? [],
      edges: overrides?.edges ?? workflow?.edges ?? [],
    }

    setCurrentWorkflow(mergedWorkflow)

    loadWorkflowData(workflowId).then(({ nodes, edges }) => {
      setCurrentWorkflow((prev) => ({
        ...prev,
        nodes,
        edges,
        updatedAt: new Date().toISOString(),
      }))
    }).catch((error) => {
      console.error('Failed to load workflow data:', error)
    })
  }

  const deployWorkflow = async (workflowId, description, csrf_token) => {
    // Immediately update the local state for instant UI feedback
    setWorkflows((prev) =>
      prev.map((workflow) =>
        workflow.id === workflowId
          ? {
              ...workflow,
              status: 'active',
              description: description || workflow.description,
              updatedAt: new Date().toISOString(),
            }
          : workflow
      )
    )

    if (currentWorkflow?.id === workflowId) {
      setCurrentWorkflow((prev) =>
        prev
          ? {
              ...prev,
              status: 'active',
              description: description || prev.description,
              updatedAt: new Date().toISOString(),
            }
          : null
      )
    }

    // Call backend API to persist changes
    try {
      // Get CSRF token
      const csrfToken = csrf_token;

      const payload = {
        status: 'active',
        description: description || currentWorkflow?.description || 'A new workflow'
      }

      const response = await fetch(`/workflows/${workflowId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error(`Failed to deploy workflow: ${response.status}`)
      }

    } catch (error) {
      console.error('Failed to deploy workflow:', error)
      // Revert local state on error
      setWorkflows((prev) =>
        prev.map((workflow) =>
          workflow.id === workflowId
            ? { ...workflow, status: 'draft' }
            : workflow
        )
      )

      if (currentWorkflow?.id === workflowId) {
        setCurrentWorkflow((prev) =>
          prev ? { ...prev, status: 'draft' } : null
        )
      }
    }
  }

  const deactivateWorkflow = async (workflowId, csrf_token) => {
    // Immediately update the local state for instant UI feedback
    setWorkflows((prev) =>
      prev.map((workflow) =>
        workflow.id === workflowId
          ? {
              ...workflow,
              status: 'inactive',
              updatedAt: new Date().toISOString(),
            }
          : workflow
      )
    )

    if (currentWorkflow?.id === workflowId) {
      setCurrentWorkflow((prev) =>
        prev
          ? {
              ...prev,
              status: 'inactive',
              updatedAt: new Date().toISOString(),
            }
          : null
      )
    }

    // Call backend API to persist changes
    try {
      // Get CSRF token
      const csrfToken = csrf_token;

      const payload = {
        status: 'inactive'
      }

      const response = await fetch(`/workflows/${workflowId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
          'Accept': 'application/json',
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        throw new Error(`Failed to deactivate workflow: ${response.status}`)
      }

    } catch (error) {
      console.error('Failed to deactivate workflow:', error)
      // Revert local state on error
      setWorkflows((prev) =>
        prev.map((workflow) =>
          workflow.id === workflowId
            ? { ...workflow, status: 'active' }
            : workflow
        )
      )

      if (currentWorkflow?.id === workflowId) {
        setCurrentWorkflow((prev) =>
          prev ? { ...prev, status: 'active' } : null
        )
      }
    }
  }

  const deleteNode = async (workflowId, nodeId, csrf_token) => {
    console.log('Deleting node:', nodeId, 'in workflow:', workflowId)
    // Find the node and store its data for potential rollback
    const workflow = workflows.find(w => w.id === workflowId)
    if (!workflow) {
      console.error('Workflow not found:', workflowId)
      return
    }

    const normalizedNodeId = String(nodeId)
    const normalizedDatabaseId = normalizedNodeId
      .replace(/^step-/, '')
      .replace(/^trigger-/, '')
    let node = workflow.nodes.find((n) => String(n.id) === normalizedNodeId)
    if (!node) {
      node = workflow.nodes.find((n) => {
        const nodeDatabaseId = n?.databaseId ?? n?.data?.databaseId
        return String(nodeDatabaseId ?? '') === normalizedDatabaseId
      })
    }
    if (!node) {
      console.error('Node not found:', nodeId, 'in workflow:', workflowId)
      return
    }
    

    // Store connected edges for potential rollback
    const connectedEdges = workflow.edges.filter(e => e.source === nodeId || e.target === nodeId)

    // Optimistic update - immediately remove from UI
    setWorkflows((prev) => {
      const updated = prev.map((workflow) => {
        if (workflow.id === workflowId) {
          const updatedWorkflow = {
            ...workflow,
            nodes: workflow.nodes.filter((n) => n.id !== nodeId),
            edges: workflow.edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
            updatedAt: new Date().toISOString(),
          }
          return updatedWorkflow
        }
        return workflow
      })
      return updated
    })

    if (currentWorkflow?.id === workflowId) {
      setCurrentWorkflow((prev) => {
        if (!prev) return null
        const updatedWorkflow = {
          ...prev,
          nodes: prev.nodes.filter((n) => n.id !== nodeId),
          edges: prev.edges.filter((e) => e.source !== nodeId && e.target !== nodeId),
          updatedAt: new Date().toISOString(),
        }
        return updatedWorkflow
      })
    }

    try {
      // Determine the database ID for task/step nodes
      let databaseId = null
      
      
      if (node.type === 'taskNode' || node.type?.endsWith('Node')) {
        // Try to get database ID from node property first
        if (node.databaseId) {
          databaseId = node.databaseId
        } 
        // If not available, try to extract from node ID (format: "step-{id}")
        else if (node.id && node.id.startsWith('step-')) {
          databaseId = node.id.replace('step-', '')
        }
      }


      // If we have a database ID, delete from backend
      if (databaseId) {
        const csrfToken = csrf_token;

        
        const response = await fetch(`/workflows/${workflowId}/steps/${databaseId}`, {
          method: 'DELETE',
          headers: {
            'X-CSRF-TOKEN': csrfToken,
            'Accept': 'application/json',
          },
        })

        
        if (!response.ok) {
          const errorText = await response.text()
          console.error('Response error:', errorText)
          throw new Error(`Failed to delete step: ${response.status} - ${errorText}`)
        }
        
      }
    } catch (error) {
      console.error('Failed to delete node from backend:', error)
      
      // Rollback - restore the node and its edges to the UI
      setWorkflows((prev) =>
        prev.map((workflow) =>
          workflow.id === workflowId
            ? {
                ...workflow,
                nodes: [...workflow.nodes, node],
                edges: [...workflow.edges, ...connectedEdges],
                updatedAt: new Date().toISOString(),
              }
            : workflow
        )
      )

      if (currentWorkflow?.id === workflowId) {
        setCurrentWorkflow((prev) =>
          prev
            ? {
                ...prev,
                nodes: [...prev.nodes, node],
                edges: [...prev.edges, ...connectedEdges],
                updatedAt: new Date().toISOString(),
              }
            : null
        )
      }
      
      throw error
    }
  }

  const deleteEdge = async (workflowId, edgeId, csrf_token) => {
    const normalizedWorkflowId = workflowId?.toString()
    const normalizedEdgeId = typeof edgeId === 'string' && edgeId.startsWith('edge-')
      ? edgeId
      : `edge-${String(edgeId)}`
    if (!normalizedWorkflowId) {
      console.error('Workflow ID is required to delete an edge')
      return
    }
    
    // Find the edge and store its data for potential rollback
    const workflow = workflows.find(w => w.id === normalizedWorkflowId)
    if (!workflow) {
      console.error('Workflow not found:', normalizedWorkflowId)
      return
    }

    const edge = workflow.edges.find(e => e.id === edgeId || e.id === normalizedEdgeId)
    if (!edge) {
      console.error('Edge not found:', edgeId, 'in workflow:', workflowId)
      return
    }

    // Check if this is a disconnection from a task node to a form node
    const sourceNode = workflow.nodes.find((n) => n.id === edge.source)
    const targetNode = workflow.nodes.find((n) => n.id === edge.target)
    
    // If source is a task node and target is a form node, clear the post_completion_form_id
    if (sourceNode?.type === 'taskNode' && targetNode?.type === 'formNode') {
      
      // Get the form ID that was being used (for logging)
      const formId = targetNode.data?.form_id
      
      // Update the task node's settings to remove the form ID
      const updatedTaskNode = {
        ...sourceNode,
        data: {
          ...sourceNode.data,
          settings: {
            ...sourceNode.data.settings,
            post_completion_form_id: null,
            capture_defer_date_in_post_completion_form: false,
            post_completion_defer_date_field_id: null,
          }
        }
      }
      
      // Update the node in the workflow context
      updateNode(normalizedWorkflowId, sourceNode.id, { data: updatedTaskNode.data })
      
      // Also update the local state immediately
      setWorkflows((prev) =>
        prev.map((w) =>
          w.id === normalizedWorkflowId
            ? {
                ...w,
                nodes: w.nodes.map((n) => n.id === sourceNode.id ? updatedTaskNode : n),
                updatedAt: new Date().toISOString(),
              }
            : w
        )
      )

      if (currentWorkflow?.id === normalizedWorkflowId) {
        setCurrentWorkflow((prev) =>
          prev
            ? {
                ...prev,
                nodes: prev.nodes.map((n) => n.id === sourceNode.id ? updatedTaskNode : n),
                updatedAt: new Date().toISOString(),
              }
            : null
        )
      }

      // Save the updated task node to the database
      try {
        const csrfToken = csrf_token;

        // Get the actual database ID for the task node
        let actualNodeId = sourceNode.id;
        if (sourceNode.id.startsWith('step-')) {
          actualNodeId = sourceNode.id.replace('step-', '');
        } else if (sourceNode.databaseId) {
          actualNodeId = sourceNode.databaseId;
        }

        const stepData = {
          step_type: 'task',
          workflow_id: normalizedWorkflowId,
          name: updatedTaskNode.data.name,
          description: updatedTaskNode.data.description,
          category: updatedTaskNode.data.category,
          priority: updatedTaskNode.data.priority,
          due: updatedTaskNode.data.due,
          status: updatedTaskNode.data.status,
          deferrable: updatedTaskNode.data.deferrable,
          missable: updatedTaskNode.data.missable,
          task_type: updatedTaskNode.data.taskTypeCategories || [],
          assignee_id: updatedTaskNode.data.assignee_id,
          link: updatedTaskNode.data.link || '',
          link_title: updatedTaskNode.data.link_title || '',
          settings: updatedTaskNode.data.settings,
          checklist: updatedTaskNode.data.checklist || []
        }

        const response = await fetch(`/workflows/${normalizedWorkflowId}/steps/${actualNodeId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': csrfToken,
            'Accept': 'application/json',
          },
          body: JSON.stringify(stepData),
        })

        if (!response.ok) {
          throw new Error(`Failed to update task node: ${response.status}`)
        }

      } catch (error) {
        console.error('Failed to save task node update to database:', error)
      }
    }
    

    // Optimistic update - immediately remove from UI
    setWorkflows((prev) => {
      const updated = prev.map((workflow) => {
        if (workflow.id === normalizedWorkflowId) {
          const updatedWorkflow = {
            ...workflow,
            edges: workflow.edges.filter((e) => e.id !== edgeId),
            updatedAt: new Date().toISOString(),
          }
          return updatedWorkflow
        }
        return workflow
      })
      return updated
    })

    if (currentWorkflow?.id === normalizedWorkflowId) {
      setCurrentWorkflow((prev) => {
        if (!prev) return null
        const updatedWorkflow = {
          ...prev,
          edges: prev.edges.filter((e) => e.id !== edgeId),
          updatedAt: new Date().toISOString(),
        }
        return updatedWorkflow
      })
    }

    try {
      // Parse the edge ID to get the database ID (format: edge-{id})
      const edgeDatabaseId = edgeId.replace('edge-', '')

      const csrfToken = csrf_token;

      
      const response = await fetch(`/workflows/${normalizedWorkflowId}/edges/${edgeDatabaseId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-TOKEN': csrfToken,
          'Accept': 'application/json',
        },
      })

      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('Response error:', errorText)
        throw new Error(`Failed to delete edge: ${response.status} - ${errorText}`)
      }
      
    } catch (error) {
      console.error('Failed to delete edge from backend:', error)
      
      // Rollback - restore the edge to the UI
      setWorkflows((prev) =>
        prev.map((workflow) =>
          workflow.id === normalizedWorkflowId
            ? {
                ...workflow,
                edges: [...workflow.edges, edge],
                updatedAt: new Date().toISOString(),
              }
            : workflow
        )
      )

      if (currentWorkflow?.id === normalizedWorkflowId) {
        setCurrentWorkflow((prev) =>
          prev
            ? {
                ...prev,
                edges: [...prev.edges, edge],
                updatedAt: new Date().toISOString(),
              }
            : null
        )
      }
      
      throw error
    }
  }

  const deleteWorkflow = async (workflowId, csrf_token) => {
    // Store the workflow before deletion for potential rollback
    const workflowToDelete = workflows.find(w => w.id === workflowId)
    
    // Optimistic update - immediately remove from UI
    setWorkflows((prev) => prev.filter((workflow) => workflow.id !== workflowId))
    
    if (currentWorkflow?.id === workflowId) {
      setCurrentWorkflow(null)
    }

    try {
      const csrfToken = csrf_token;

      const response = await fetch(`/workflows/${workflowId}`, {
        method: 'DELETE',
        headers: {
          'X-CSRF-TOKEN': csrfToken,
          'Accept': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to delete workflow: ${response.status}`)
      }

    } catch (error) {
      console.error('Failed to delete workflow from backend:', error)
      
      // Rollback - restore the workflow to the UI
      if (workflowToDelete) {
        setWorkflows((prev) => [...prev, workflowToDelete])
        
        // If it was the current workflow, restore it
        if (workflowToDelete.id === workflowId) {
          setCurrentWorkflow(workflowToDelete)
        }
      }
      
      throw error
    }
  }

  const syncNodeTriggers = async (workflowId, nodeId, triggers, position = null, csrf_token) => {
    try {
      const csrfToken = csrf_token;

      const requestPayload = {
        triggers: triggers,
        position: position
      };

      const response = await fetch(`/workflows/${workflowId}/nodes/${nodeId}/triggers/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
          'Accept': 'application/json',
        },
        body: JSON.stringify(requestPayload),
      })

      if (!response.ok) {
        throw new Error(`Failed to sync triggers: ${response.status}`)
      }

      const data = await response.json();
      
      // Update the workflow context to reflect the new trigger state
      const enabledTriggers = triggers.filter(t => t.enabled).map(trigger => ({
        id: `${trigger.type}-${trigger.id || Date.now()}`,
        type: trigger.type,
        name: trigger.name,
        description: trigger.description,
        enabled: trigger.enabled,
        config: trigger.config
      }));
      
      // Update the current workflow's nodes to reflect the new trigger state
      if (currentWorkflow?.id === workflowId) {
        setCurrentWorkflow(prev => {
          if (!prev) return prev;
          
          const updatedNodes = prev.nodes.map(node => {
            if (node.id === nodeId) {
              return {
                ...node,
                data: {
                  ...node.data,
                  enabledTriggers: enabledTriggers
                }
              };
            }
            return node;
          });
          
          return {
            ...prev,
            nodes: updatedNodes,
            updatedAt: new Date().toISOString()
          };
        });
      }
      
      // Update the workflows list as well
      setWorkflows(prev => prev.map(workflow => {
        if (workflow.id === workflowId) {
          const updatedNodes = workflow.nodes.map(node => {
            if (node.id === nodeId) {
              return {
                ...node,
                data: {
                  ...node.data,
                  enabledTriggers: enabledTriggers
                }
              };
            }
            return node;
          });
          
          return {
            ...workflow,
            nodes: updatedNodes,
            updatedAt: new Date().toISOString()
          };
        }
        return workflow;
      }));
      
      return data.triggers;
    } catch (error) {
      console.error('Failed to sync triggers:', error)
      throw error;
    }
  }

  const getNodeTriggers = async (workflowId, nodeId, csrf_token) => {
    try {
      const csrfToken = csrf_token;

      const response = await fetch(`/workflows/${workflowId}/nodes/${nodeId}/triggers`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-TOKEN': csrfToken,
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to get triggers: ${response.status}`)
      }

      const triggers = await response.json();
      return triggers;
    } catch (error) {
      // Don't log network errors as they're usually temporary
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        console.warn('Network error while fetching triggers, will retry later');
      } else {
        console.error('Failed to get triggers:', error)
      }
      return [];
    }
  }

  const refreshWorkflowData = async (workflowId, csrf_token) => {
    try {
      
      const response = await fetch(`/workflows/${workflowId}/nodes-and-edges`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-TOKEN': csrf_token,
        },
      })
      
      if (response.ok) {
        const { nodes, edges } = await response.json()
        
        // Update the workflow in state with fresh data
        setWorkflows(prev => {
          const updated = prev.map(w => 
            w.id === workflowId.toString() 
              ? { ...w, nodes, edges, updatedAt: new Date().toISOString() }
              : w
          );
          return updated;
        })
        
        // Update current workflow if it's the active one
        if (currentWorkflow?.id === workflowId.toString()) {
          setCurrentWorkflow(prev => 
            prev ? { ...prev, nodes, edges, updatedAt: new Date().toISOString() } : null
          )
        }
        
        return { nodes, edges }
      } else {
        console.error(`Failed to refresh workflow data: ${response.status}`)
      }
    } catch (error) {
      console.error('Error refreshing workflow data:', error)
    }
    return { nodes: [], edges: [] }
  }

  const copyNode = (nodeId, workflowId) => {
    const workflow = workflows.find(w => w.id === workflowId) || currentWorkflow
    if (!workflow) return false
    
    const normalizedNodeId = String(nodeId)
    const normalizedDatabaseId = normalizedNodeId
      .replace(/^step-/, '')
      .replace(/^trigger-/, '')
    let node = workflow.nodes.find((n) => String(n.id) === normalizedNodeId)
    if (!node) {
      node = workflow.nodes.find((n) => {
        const databaseId = n?.databaseId ?? n?.data?.databaseId
        return String(databaseId ?? '') === normalizedDatabaseId
      })
    }
    if (!node) return false
    
    // Store the copied node in localStorage for cross-workflow copying
    const nodeCopy = {
      ...node,
      id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      databaseId: undefined,
      position: { x: 0, y: 0 },
      selected: false,
      data: {
        ...node.data,
        expanded: true
      }
    }
    
    if (typeof window !== 'undefined') {
      localStorage.setItem('workflow-clipboard', JSON.stringify({
        node: nodeCopy,
        workflowId: workflowId,
        timestamp: Date.now()
      }))
    }
    
    return true
  }

  const pasteNode = async (targetWorkflowId, position = { x: 100, y: 100 }, csrf_token) => {
    if (typeof window === 'undefined') return null
    
    const stored = localStorage.getItem('workflow-clipboard')
    if (!stored) return null
    
    try {
      const { node, workflowId, timestamp } = JSON.parse(stored)
      
      // Check if clipboard is still valid (24 hours)
      if (Date.now() - timestamp > 24 * 60 * 60 * 1000) {
        localStorage.removeItem('workflow-clipboard')
        return null
      }
      
      // Create a fresh copy with a new ID
      const pastedNode = {
        ...node,
        id: `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        position: position,
        selected: false,
        data: {
          ...node.data,
          expanded: true
        }
      }
      
      // Add the node to the target workflow
      const addedNode = await addNode(targetWorkflowId, pastedNode, csrf_token, {
        skipLocalState: true,
        requirePersistence: true,
      })
      if (addedNode) {
        await refreshWorkflowData(targetWorkflowId)
      }
      
      return addedNode
    } catch (error) {
      console.error('Failed to paste node:', error)
      return null
    }
  }

  const hasCopiedNode = () => {
    if (typeof window === 'undefined') return false
    
    const stored = localStorage.getItem('workflow-clipboard')
    if (!stored) return false
    
    try {
      const { timestamp } = JSON.parse(stored)
      return Date.now() - timestamp < 24 * 60 * 60 * 1000
    } catch {
      return false
    }
  }

  const getCopiedNodeInfo = () => {
    if (typeof window === 'undefined') return null
    
    const stored = localStorage.getItem('workflow-clipboard')
    if (!stored) return null
    
    try {
      const { node, workflowId, timestamp } = JSON.parse(stored)
      
      if (Date.now() - timestamp > 24 * 60 * 60 * 1000) {
        localStorage.removeItem('workflow-clipboard')
        return null
      }
      
      return {
        node,
        workflowId,
        nodeType: node.type,
        nodeName: node.data?.name || node.data?.label || 'Unnamed Node'
      }
    } catch {
      return null
    }
  }

  return (
    <WorkflowContext.Provider
      value={{
        workflows,
        currentWorkflow,
        createWorkflow,
        updateWorkflow,
        addNode,
        updateNode,
        addEdge,
        updateEdge,
        addNodes,
        addEdges,
        validateConnection,
        setCurrentWorkflow: setCurrentWorkflowById,
        deployWorkflow,
        deactivateWorkflow,
        deleteWorkflow,
        syncNodeTriggers,
        getNodeTriggers,
        loadWorkflowData,
        debouncedSave,
        deleteNode,
        deleteEdge,
        setWorkflows,
        refreshWorkflowData,
        copyNode,
        pasteNode,
        hasCopiedNode,
        getCopiedNodeInfo,
      }}
    >
      {children}
    </WorkflowContext.Provider>
  )
}

export function useWorkflow() {
  const context = useContext(WorkflowContext)
  if (context === undefined) {
    throw new Error('useWorkflow must be used within a WorkflowProvider')
  }
  return context
}